/**
 * The muscle growth pathway's two clinician-only cards (Brian, 29 September):
 * recovery intelligence above the plan, and the Twin match score under the
 * Clinical Twin prediction. Both render nothing unless the patient is on that
 * pathway, so the standard treatment plan screen is unchanged for everyone else.
 *
 * Moved off the standalone treatment plan page when that page was retired in
 * favour of the dashboard's patient panel.
 */

import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';

interface RecoveryIntelligence {
  primaryGoal: string | null;
  goalConfirmed: boolean;
  rankedGoals: string[];
  recovery: {
    eligible: boolean;
    reasons: string[];
    recoveryScore: number | null;
    problems: string[];
    injury: boolean | null;
    injuryLocations: string[];
  } | null;
  adjuncts: Array<{ therapyId: string; name: string; status: 'no_signal' | 'not_offered' | 'candidate' }>;
}

const REASON_TEXT: Record<string, string> = {
  recovery_score_low: 'Low exercise recovery score',
  injury_limits_training: 'Pain or injury limits training',
};

function reasonText(reason: string): string {
  return REASON_TEXT[reason] ?? (reason.startsWith('recovery_problem:') ? reason.slice('recovery_problem:'.length) : reason);
}

const ADJUNCT_STATUS: Record<RecoveryIntelligence['adjuncts'][number]['status'], string> = {
  candidate: 'Recovery adjunct candidate',
  not_offered: 'Eligible, but not offered at GLO',
  no_signal: 'Not evaluated: no recovery or injury signal',
};

const COMPONENT_LABEL: Record<string, string> = {
  patientSimilarity: 'Patient similarity',
  goalOutcomeMatch: 'Goal outcome',
  behavioralMatch: 'Training and behaviour',
  secondaryGoalMatch: 'Second and third goals',
  evidenceStrength: 'Evidence strength',
};

/**
 * Recovery intelligence, above the plan (Brian, 29 Sept, STEP 6): the
 * recovery issue, and each recovery adjunct with why it is or is not a
 * candidate. MCOS reports; "the physician, not MCOS, makes the final
 * treatment selection".
 */
export function RecoveryCard({ patientId }: { patientId: string }) {
  const query = useQuery({
    queryKey: ['recovery-intelligence', patientId],
    queryFn: () => api.get<RecoveryIntelligence>(`/patients/${patientId}/recovery-intelligence`),
  });
  const data = query.data;
  if (!data?.recovery) return null;
  const { recovery } = data;
  return (
    <div className="mb-2.5 rounded-md2 border border-amber-200 bg-amber-50/60 px-5 py-4" data-testid="recovery-card">
      <p className="text-xs uppercase tracking-wide text-ink-muted">Recovery intelligence</p>
      <p className="mt-1 text-sm text-ink">
        <span className="font-semibold">Recovery issue: </span>
        {recovery.eligible ? recovery.reasons.map(reasonText).join('; ') : 'None reported'}
      </p>
      {recovery.recoveryScore !== null && (
        <p className="mt-0.5 text-xs text-ink-muted">Exercise recovery {recovery.recoveryScore} of 10</p>
      )}
      <ul className="mt-3 space-y-1">
        {data.adjuncts.map((adjunct) => (
          <li key={adjunct.therapyId} className="flex flex-wrap items-center justify-between gap-2 text-sm">
            <span className="font-medium text-ink">{adjunct.name}</span>
            <span className="text-xs text-ink-muted">{ADJUNCT_STATUS[adjunct.status]}</span>
          </li>
        ))}
      </ul>
      <p className="mt-3 text-[11px] text-ink-muted">
        A recovery adjunct is evaluated only for recovery, never as a muscle building therapy. The
        clinician makes the final choice.
      </p>
    </div>
  );
}

/** "Twin match, muscle growth": the score and the parts it is made of. Muscle pathway only. */
export function TwinMatchCard({ match }: { match: { score: number; components: Record<string, number | null> } | null | undefined }) {
  if (!match) return null;
  return (
    <div className="mt-3 rounded-control border border-ink/10 px-3 py-2.5" data-testid="twin-match">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-sm text-ink-muted">Twin match, muscle growth</span>
        <span className="text-lg font-semibold text-ink">{match.score}</span>
      </div>
      <dl className="mt-1.5 space-y-0.5 text-[11px]">
        {Object.entries(match.components)
          .filter(([, value]) => value !== null)
          .map(([key, value]) => (
            <div key={key} className="flex justify-between gap-3">
              <dt className="text-ink-muted">{COMPONENT_LABEL[key] ?? key}</dt>
              <dd className="text-ink">{value}</dd>
            </div>
          ))}
      </dl>
    </div>
  );
}
